// Blend mode for graph materials (Unreal-style): opaque | masked | translucent.
//
// Opacity masking is material-level and shader-agnostic (same in Blender and
// three.js): the graph's `opacity` output writes diffuseColor.a, and these
// render props decide what happens to that alpha — hard-discard (masked) or
// smooth blend (translucent). This mapping is kept dependency-free so it
// unit-tests without pulling in THREE; buildGraphMaterial applies the result
// plus `side`/depth. Standard pbr/toon/skin materials use the legacy
// `blendType` path in avatarMaterial.js instead.
//
//   opaque      → alpha ignored
//   masked      → discard where alpha < clip value (alphaTest)   [UE Masked / glTF MASK]
//   translucent → alpha blend (transparent)                      [UE Translucent / glTF BLEND]

export const GRAPH_BLEND_MODES = ['opaque', 'masked', 'translucent']

// Default clip value — matches Unreal's material default
// (OpacityMaskClipValue = 0.3333f, Material.cpp).
export const DEFAULT_CLIP = 0.3333

// Map an output-node `data` object ({ blendMode, clip }) to the THREE material
// render props. Returns { transparent, alphaTest }. Unknown modes → opaque.
// `clip` is the masked cutoff (Unreal Opacity Mask Clip Value / glTF alphaCutoff).
// `alphaToCoverage` (masked only): instead of a hard cut at the clip value the
// alpha drives the MSAA coverage mask, so an edge that is 40% covered writes
// 40% of its samples and the cut reads as a soft edge — what hair cards need,
// where a 4096 atlas of thin strands is otherwise a comb of hard slivers.
// Costs nothing when the canvas has no MSAA (three ignores the flag), and it
// is deliberately not the default: an opaque-with-holes mesh (a fence, a leaf
// card) is what masked has always meant.
export function graphBlendMode(d = {}) {
  const mode = d.blendMode ?? 'opaque'
  if (mode === 'masked') {
    return { transparent: false, alphaTest: d.clip ?? DEFAULT_CLIP, alphaToCoverage: d.alphaToCoverage === true }
  }
  if (mode === 'translucent') {
    // `cutout` (translucent only): a blend that still DISCARDS below the clip
    // value — hair cards, where the coverage atlas's soft strand edges want to
    // blend but the near-zero alpha between strands must not write depth or a
    // ghost tint. Opt-in and explicit: every translucent row already carries a
    // default `clip` from the importer, so honouring clip on translucent by
    // itself would silently cut every existing translucent material.
    return { transparent: true, alphaTest: d.cutout === true ? (d.clip ?? DEFAULT_CLIP) : 0, alphaToCoverage: false }
  }
  return { transparent: false, alphaTest: 0, alphaToCoverage: false }
}
